import {
  ColorMappedCell,
  DefaultHeader,
  CombinedHeader,
  CombinedCell,
  DateCell,
  SeverityChipCell,
  WorkStatusChipCell,
  AvatarCell,
  DefaultCell,
  CaseTableLinkCell,
  TruncatedCell,
  TruncatedCellLines
} from "components/SharedTable/tableRenderers";

export const CASE_TABLE_COLUMNS = [
  {
    Header: data =>
      DefaultHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Priority"
      }),
    title: "Priority",
    accessor: "priority_name",
    id: "priority_name",
    Cell: data => {
      // Show empty cells for cells in row that aren't the grouped column. i.e the expandable/collapsible row
      if (data.row.isGrouped && !data.cell.isGrouped) {
        return null;
      }
      return ColorMappedCell({ value: data.cell.value });
    }
  },
  {
    Header: data =>
      DefaultHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Case ID"
      }),
    title: "Case ID",
    accessor: "display_id",
    id: "display_id",
    disableGroupBy: true,
    Cell: data => {
      // Show empty cells for cells in row that aren't the grouped column. i.e the expandable/collapsible row
      if (data.row.isGrouped && !data.cell.isGrouped) {
        return null;
      }
      return CaseTableLinkCell({
        value: data.cell.value,
        caseId: data.cell.row.original.case_id
      });
    }
  },
  {
    Header: data =>
      DefaultHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Status"
      }),
    title: "Status",
    accessor: "case_status_name",
    id: "case_status_name",
    Cell: data => {
      // Show empty cells for cells in row that aren't the grouped column. i.e the expandable/collapsible row
      if (data.row.isGrouped && !data.cell.isGrouped) {
        return null;
      }
      return TruncatedCellLines({ value: data.cell.value });
    }
  },
  {
    Header: data =>
      CombinedHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Site",
        subHeader: "Machine"
      }),
    title: "Site / Machine",
    id: "site_machine",
    accessor: data => [data.site_name, data.machine_name],
    Cell: data => {
      // Show empty cells for cells in row that aren't the grouped column. i.e the expandable/collapsible row
      if (data.row.isGrouped && !data.cell.isGrouped) {
        return null;
      }
      return CombinedCell({ value: data.cell.value });
    }
  },
  {
    Header: "Site",
    id: "site_name",
    accessor: "site_name"
  },
  {
    Header: "Machine",
    id: "machine_name",
    accessor: "machine_name"
  },
  {
    Header: data =>
      CombinedHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Manufacturer",
        subHeader: "Model"
      }),
    title: "Manufacturer / Model",
    id: "manufacturer_model",
    accessor: data => [data.manufacturer_name, data.model_name],
    Cell: data => {
      // Show empty cells for cells in row that aren't the grouped column. i.e the expandable/collapsible row
      if (data.row.isGrouped && !data.cell.isGrouped) {
        return null;
      }
      return CombinedCell({ value: data.cell.value });
    }
  },
  {
    Header: data =>
      CombinedHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Component",
        subHeader: "Failure Mode"
      }),
    title: "Component / Failure Mode",
    id: "component_failure_mode",
    accessor: data => [data.component_name, data.failure_mode_name],
    Cell: data => {
      // Show empty cells for cells in row that aren't the grouped column. i.e the expandable/collapsible row
      if (data.row.isGrouped && !data.cell.isGrouped) {
        return null;
      }
      return CombinedCell({ value: data.cell.value });
    }
  },
  {
    Header: "Component",
    id: "component_name",
    accessor: "component_name"
  },
  {
    Header: "Failure Mode",
    id: "failure_mode_name",
    accessor: "failure_mode_name"
  },
  {
    Header: data =>
      DefaultHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Machine\nStatus"
      }),
    title: "Machine Status",
    accessor: "machine_status_name",
    id: "machine_status_name",
    Cell: data => {
      // Show empty cells for cells in row that aren't the grouped column. i.e the expandable/collapsible row
      if (data.row.isGrouped && !data.cell.isGrouped) {
        return null;
      }

      return ColorMappedCell({ value: data.cell.value });
    }
  },
  {
    Header: data =>
      DefaultHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Diagnostics"
      }),
    title: "Diagnostics",
    accessor: data => [
      data.diagnostic_severity_name,
      data.diagnostic_severity_colour
    ],
    id: "diagnostic_severity",
    Cell: data => {
      // Show empty cells for cells in row that aren't the grouped column. i.e the expandable/collapsible row
      if (data.row.isGrouped && !data.cell.isGrouped) {
        return null;
      }

      const [name, color] = data.cell.value;

      return SeverityChipCell({ name, color });
    }
  },
  {
    Header: data =>
      DefaultHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Inspection"
      }),
    title: "Inspection",
    accessor: data => [
      data.inspection_severity_name,
      data.inspection_severity_colour
    ],
    id: "inspection_severity",
    Cell: data => {
      // Show empty cells for cells in row that aren't the grouped column. i.e the expandable/collapsible row
      if (data.row.isGrouped && !data.cell.isGrouped) {
        return null;
      }

      const [name, color] = data.cell.value;

      return SeverityChipCell({ name, color });
    }
  },
  {
    Header: data =>
      DefaultHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Assignee"
      }),
    title: "Assignee",
    accessor: data => [data.assignee_first_name, data.assignee_last_name],
    id: "assignee_name",
    Cell: data => {
      // Show empty cells for cells in row that aren't the grouped column. i.e the expandable/collapsible row
      if (data.row.isGrouped && !data.cell.isGrouped) {
        return null;
      }

      const [firstName, lastName] = data.cell.value;

      return AvatarCell({ firstName, lastName });
    }
  },
  {
    Header: data =>
      DefaultHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Opened"
      }),
    title: "Opened",
    accessor: data => {
      if (!data.open_date) {
        return null;
      }

      const [date] = data.open_date.split("T");

      return date ?? null;
    },
    id: "open_date",
    Cell: data => {
      // Show empty cells for cells in row that aren't the grouped column. i.e the expandable/collapsible row
      if (data.row.isGrouped && !data.cell.isGrouped) {
        return null;
      }

      if (data.row.isGrouped) {
        return DateCell({ value: data.cell.value });
      }

      return DateCell({ value: data.row.original.open_date });
    }
  },
  {
    Header: data =>
      DefaultHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Created By"
      }),
    title: "Created By",
    accessor: data => [data.created_by_first_name, data.created_by_last_name],
    id: "created_by",
    Cell: data => {
      // Show empty cells for cells in row that aren't the grouped column. i.e the expandable/collapsible row
      if (data.row.isGrouped && !data.cell.isGrouped) {
        return null;
      }

      const [firstName, lastName] = data.cell.value;

      return AvatarCell({ firstName, lastName });
    }
  },
  {
    Header: data =>
      DefaultHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Updated"
      }),
    title: "Updated",
    accessor: data => {
      if (!data.last_updated) {
        return null;
      }

      const [date] = data.last_updated.split("T");

      return date ?? null;
    },
    id: "last_updated",
    Cell: data => {
      // Show empty cells for cells in row that aren't the grouped column. i.e the expandable/collapsible row
      if (data.row.isGrouped && !data.cell.isGrouped) {
        return null;
      }

      if (data.row.isGrouped) {
        return DateCell({ value: data.cell.value });
      }

      return DateCell({ value: data.row.original.last_updated });
    }
  },
  {
    Header: data =>
      DefaultHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Next Action"
      }),
    title: "Next Action",
    accessor: "case_action_name",
    id: "case_action_name",
    Cell: data => {
      // Show empty cells for cells in row that aren't the grouped column. i.e the expandable/collapsible row
      if (data.row.isGrouped && !data.cell.isGrouped) {
        return null;
      }

      return DefaultCell({ value: data.cell.value });
    }
  },
  {
    Header: data =>
      DefaultHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Next Action due"
      }),
    title: "Next Action Due",
    accessor: "case_action_due",
    id: "case_action_due",
    Cell: data => {
      // Show empty cells for cells in row that aren't the grouped column. i.e the expandable/collapsible row
      if (data.row.isGrouped && !data.cell.isGrouped) {
        return null;
      }

      return DateCell({ value: data.cell.value });
    }
  },
  {
    Header: data =>
      DefaultHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Work Request Status"
      }),
    title: "Work Request Status",
    accessor: "latest_work_order_status",
    id: "latest_work_order_status",
    Cell: data => {
      // Show empty cells for cells in row that aren't the grouped column. i.e the expandable/collapsible row
      if (data.row.isGrouped && !data.cell.isGrouped) {
        return null;
      }

      return WorkStatusChipCell({ value: data.cell.value });
    }
  },
  {
    Header: data =>
      DefaultHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Labels"
      }),
    title: "Labels",
    accessor: "labels",
    disableGroupBy: true,
    id: "labels",
    Cell: data => {
      // Show empty cells for cells in row that aren't the grouped column. i.e the expandable/collapsible row
      if (data.row.isGrouped && !data.cell.isGrouped) {
        return null;
      }

      return TruncatedCell({
        value: data.cell.value.map(el => el.name).join(", ") || "-"
      });
    },
    disableSortBy: true
  },
  {
    Header: data =>
      DefaultHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Description"
      }),
    title: "Description",
    id: "description",
    accessor: data => data.description,
    Cell: data => {
      // Show empty cells for cells in row that aren't the grouped column. i.e the expandable/collapsible row
      if (data.row.isGrouped && !data.cell.isGrouped) {
        return null;
      }

      return TruncatedCell({ value: data.cell.value });
    }
  },
  {
    Header: data =>
      DefaultHeader({
        sortBy: data.state.sortBy,
        column: data.column,
        header: "Recommendation"
      }),
    title: "Recommendation",
    id: "recommendation",
    accessor: data => data.recommendation,
    Cell: data => {
      // Show empty cells for cells in row that aren't the grouped column. i.e the expandable/collapsible row
      if (data.row.isGrouped && !data.cell.isGrouped) {
        return null;
      }

      return TruncatedCell({ value: data.cell.value || "-" });
    }
  }
];

// These columns should always remain hidden, they are used for ordering and
// filtering combined columns or columns that retun JSX for cell values
export const CONSTANT_HIDDEN_COLUMNS = [
  "site_name",
  "machine_name",
  "component_name",
  "failure_mode_name"
];

export const INITIAL_HIDDEN_COLUMNS = [
  "case_action_due",
  "case_action_name",
  "manufacturer_model"
];

export const CONDITIONALLY_HIDDEN_COLUMNS = ["latest_work_order_status"];

export const DEFAULT_SORT = [
  { id: "priority_name", desc: true },
  { id: "display_id", desc: false }
];

export const SAVED_TABLE_VIEW_PARAMETER_NAME = "tableview";
